import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, ChevronLeft, Users, DollarSign, TrendingUp, Settings,
  Plus, Edit2, Trash2, Save, X, Award, Star, Crown,
  UserPlus, BarChart3, Gift, Check, AlertCircle, Search,
  ChevronDown, ChevronUp, Mail, Phone, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default commission structure (can be overridden per person)
const DEFAULT_COMMISSION = {
  establishmentPercent: 75,
  trailingPercent: 15,
  retention6MonthPercent: 10,
  retention12MonthPercent: 15
};

// Role icons and colors
const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700', icon: Crown },
  manager: { label: 'Sales Manager', color: 'bg-blue-100 text-blue-700', icon: Star },
  senior: { label: 'Senior Rep', color: 'bg-green-100 text-green-700', icon: Award },
  rep: { label: 'Sales Rep', color: 'bg-gray-100 text-gray-700', icon: Users }
};

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === 'lionheart2024') {
      onLogin();
    } else {
      setError('Invalid admin password');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF9] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-8 max-w-md w-full shadow-lg border border-[#EBE8E0]"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Sales Admin
          </h1>
          <p className="text-[#7A7571] mt-2">Owner access only</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="Enter admin password"
              className="rounded-xl h-12"
            />
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </div>
          <Button
            type="submit"
            className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold"
          >
            Access Admin Panel
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

// Team Member Card
const TeamMemberCard = ({ member, onEdit, onDelete, onAddBonus }) => {
  const [expanded, setExpanded] = useState(false);
  const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.rep;
  const RoleIcon = roleConfig.icon;

  const totalEarnings = (member.earnings?.startup || 0) + 
                        (member.earnings?.trailing || 0) + 
                        (member.earnings?.bonuses || 0);

  return (
    <motion.div
      layout
      className="bg-white rounded-2xl border border-[#EBE8E0] overflow-hidden"
    >
      <div 
        className="p-5 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl ${roleConfig.color} flex items-center justify-center`}>
              <RoleIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-[#1A1817]">{member.name}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${roleConfig.color}`}>
                {roleConfig.label}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-green-600">${totalEarnings.toLocaleString()}</div>
            <div className="text-xs text-[#7A7571]">Total Earnings</div>
          </div>
        </div>

        {/* Commission Structure Preview */}
        <div className="mt-4 grid grid-cols-4 gap-2 text-center">
          <div className="bg-[#F5F3EE] rounded-lg p-2">
            <div className="text-lg font-bold text-[#1A1817]">{member.commission?.establishmentPercent || DEFAULT_COMMISSION.establishmentPercent}%</div>
            <div className="text-xs text-[#7A7571]">Startup</div>
          </div>
          <div className="bg-[#F5F3EE] rounded-lg p-2">
            <div className="text-lg font-bold text-[#1A1817]">{member.commission?.trailingPercent || DEFAULT_COMMISSION.trailingPercent}%</div>
            <div className="text-xs text-[#7A7571]">Trailing</div>
          </div>
          <div className="bg-[#F5F3EE] rounded-lg p-2">
            <div className="text-lg font-bold text-[#1A1817]">{member.deals || 0}</div>
            <div className="text-xs text-[#7A7571]">Deals</div>
          </div>
          <div className="bg-[#F5F3EE] rounded-lg p-2">
            <div className="text-lg font-bold text-purple-600">${member.earnings?.bonuses || 0}</div>
            <div className="text-xs text-[#7A7571]">Bonuses</div>
          </div>
        </div>

        <div className="flex items-center justify-center mt-3 text-[#7A7571]">
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-[#EBE8E0] bg-[#FDFCF9]"
          >
            <div className="p-5 space-y-4">
              {/* Contact Info */}
              <div className="flex flex-wrap gap-4 text-sm text-[#7A7571]">
                {member.email && (
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {member.email}
                  </div>
                )}
                {member.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {member.phone}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Joined {new Date(member.joinedAt || Date.now()).toLocaleDateString()}
                </div>
              </div>

              {/* Detailed Commission Structure */}
              <div className="bg-white rounded-xl p-4 border border-[#EBE8E0]">
                <h4 className="font-semibold text-[#1A1817] mb-3">Commission Structure</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#7A7571]">Establishment Fee</span>
                    <span className="font-semibold">{member.commission?.establishmentPercent || DEFAULT_COMMISSION.establishmentPercent}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7A7571]">Monthly Trailing</span>
                    <span className="font-semibold">{member.commission?.trailingPercent || DEFAULT_COMMISSION.trailingPercent}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7A7571]">6-Month Retention Bonus</span>
                    <span className="font-semibold">{member.commission?.retention6MonthPercent || DEFAULT_COMMISSION.retention6MonthPercent}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7A7571]">12-Month Retention Bonus</span>
                    <span className="font-semibold">{member.commission?.retention12MonthPercent || DEFAULT_COMMISSION.retention12MonthPercent}%</span>
                  </div>
                </div>
              </div>

              {/* Bonus History */}
              {member.bonusHistory && member.bonusHistory.length > 0 && (
                <div className="bg-white rounded-xl p-4 border border-[#EBE8E0]">
                  <h4 className="font-semibold text-[#1A1817] mb-3">Bonus History</h4>
                  <div className="space-y-2">
                    {member.bonusHistory.map((bonus, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <div>
                          <span className="text-[#1A1817]">{bonus.reason}</span>
                          <span className="text-[#7A7571] ml-2">
                            {new Date(bonus.date).toLocaleDateString()}
                          </span>
                        </div>
                        <span className="font-semibold text-green-600">+${bonus.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => onEdit(member)}
                  variant="outline"
                  className="flex-1 rounded-xl border-[#EBE8E0]"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={() => onAddBonus(member)}
                  className="flex-1 rounded-xl bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Add Bonus
                </Button>
                <Button
                  onClick={() => onDelete(member)}
                  variant="outline"
                  className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Add/Edit Member Modal
const MemberModal = ({ isOpen, onClose, member, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'rep',
    commission: { ...DEFAULT_COMMISSION }
  });

  useEffect(() => {
    if (member) {
      setForm({
        ...member,
        commission: member.commission || { ...DEFAULT_COMMISSION }
      });
    } else {
      setForm({
        name: '',
        email: '',
        phone: '',
        role: 'rep',
        commission: { ...DEFAULT_COMMISSION }
      });
    }
  }, [member]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error('Name is required');
      return;
    }
    onSave(form);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            {member ? 'Edit Team Member' : 'Add Team Member'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Name *</label>
            <Input
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Full name"
              className="rounded-xl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-2">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="email@example.com"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-2">Phone</label>
              <Input
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="0400 000 000"
                className="rounded-xl"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Role</label>
            <select
              value={form.role}
              onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
              className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0]"
            >
              <option value="rep">Sales Rep</option>
              <option value="senior">Senior Rep</option>
              <option value="manager">Sales Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Commission Structure */}
          <div className="bg-[#F5F3EE] rounded-xl p-4">
            <h3 className="font-semibold text-[#1A1817] mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#FF7555]" />
              Individual Commission Rates
            </h3>
            <p className="text-sm text-[#7A7571] mb-4">
              Set custom rates for this team member based on their performance and merit.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#7A7571] mb-1">Startup Commission %</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.commission.establishmentPercent}
                  onChange={e => setForm(p => ({ 
                    ...p, 
                    commission: { ...p.commission, establishmentPercent: parseInt(e.target.value) || 0 }
                  }))}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-xs text-[#7A7571] mb-1">Trailing Commission %</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.commission.trailingPercent}
                  onChange={e => setForm(p => ({ 
                    ...p, 
                    commission: { ...p.commission, trailingPercent: parseInt(e.target.value) || 0 }
                  }))}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-xs text-[#7A7571] mb-1">6-Month Retention %</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.commission.retention6MonthPercent}
                  onChange={e => setForm(p => ({ 
                    ...p, 
                    commission: { ...p.commission, retention6MonthPercent: parseInt(e.target.value) || 0 }
                  }))}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-xs text-[#7A7571] mb-1">12-Month Retention %</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.commission.retention12MonthPercent}
                  onChange={e => setForm(p => ({ 
                    ...p, 
                    commission: { ...p.commission, retention12MonthPercent: parseInt(e.target.value) || 0 }
                  }))}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-semibold"
          >
            <Save className="w-4 h-4 mr-2" />
            {member ? 'Save Changes' : 'Add Team Member'}
          </Button>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Add Bonus Modal
const BonusModal = ({ isOpen, onClose, member, onSave }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!amount || !reason) {
      toast.error('Amount and reason are required');
      return;
    }
    onSave({
      memberId: member.id,
      amount: parseFloat(amount),
      reason,
      date: new Date().toISOString()
    });
    setAmount('');
    setReason('');
  };

  if (!isOpen || !member) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Award Bonus
            </h2>
            <p className="text-[#7A7571]">For {member.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Bonus Amount ($)</label>
            <Input
              type="number"
              min="0"
              step="50"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="500"
              className="rounded-xl text-2xl font-bold text-center h-16"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Reason for Bonus</label>
            <select
              value={reason}
              onChange={e => setReason(e.target.value)}
              className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0] mb-2"
            >
              <option value="">Select reason...</option>
              <option value="Exceeded monthly target">Exceeded monthly target</option>
              <option value="Closed high-value deal">Closed high-value deal</option>
              <option value="Outstanding customer feedback">Outstanding customer feedback</option>
              <option value="Team leadership">Team leadership</option>
              <option value="New record achievement">New record achievement</option>
              <option value="Mentoring new team members">Mentoring new team members</option>
              <option value="Other">Other (specify below)</option>
            </select>
            {reason === 'Other' && (
              <Input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Specify reason..."
                className="rounded-xl"
              />
            )}
          </div>

          <div className="bg-green-50 rounded-xl p-4 text-center">
            <Gift className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-green-700 font-medium">
              This will add ${amount || '0'} to {member.name}'s bonus earnings
            </p>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold"
          >
            <Gift className="w-4 h-4 mr-2" />
            Award Bonus
          </Button>
        </form>
      </motion.div>
    </motion.div>
  );
};

const SalesAdmin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchTeam();
    }
  }, [isAuthenticated]);

  const fetchTeam = async () => {
    try {
      const response = await fetch(`${API_URL}/api/puppoto/sales-team?password=lionheart2024`);
      if (response.ok) {
        const data = await response.json();
        setTeam(data.team || []);
      }
    } catch (error) {
      console.error('Failed to fetch team:', error);
      // Load demo data if API fails
      setTeam([
        {
          id: '1',
          name: 'Pippa',
          email: 'pippa@puppoto.com',
          phone: '0400 000 001',
          role: 'manager',
          commission: { establishmentPercent: 80, trailingPercent: 20, retention6MonthPercent: 12, retention12MonthPercent: 18 },
          deals: 15,
          earnings: { startup: 11250, trailing: 4500, bonuses: 2000 },
          bonusHistory: [
            { amount: 1000, reason: 'Exceeded monthly target', date: '2026-02-15' },
            { amount: 1000, reason: 'Team leadership', date: '2026-03-01' }
          ],
          joinedAt: '2026-01-01'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMember = async (memberData) => {
    try {
      const isEdit = !!memberData.id;
      const response = await fetch(`${API_URL}/api/puppoto/sales-team/member`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...memberData, password: 'lionheart2024' })
      });

      if (response.ok) {
        toast.success(isEdit ? 'Team member updated!' : 'Team member added!');
        fetchTeam();
      } else {
        // Handle locally if API fails
        if (isEdit) {
          setTeam(prev => prev.map(m => m.id === memberData.id ? memberData : m));
        } else {
          setTeam(prev => [...prev, { ...memberData, id: Date.now().toString(), deals: 0, earnings: { startup: 0, trailing: 0, bonuses: 0 }, joinedAt: new Date().toISOString() }]);
        }
        toast.success(isEdit ? 'Team member updated!' : 'Team member added!');
      }
    } catch (error) {
      // Handle locally
      if (memberData.id) {
        setTeam(prev => prev.map(m => m.id === memberData.id ? memberData : m));
      } else {
        setTeam(prev => [...prev, { ...memberData, id: Date.now().toString(), deals: 0, earnings: { startup: 0, trailing: 0, bonuses: 0 }, joinedAt: new Date().toISOString() }]);
      }
      toast.success(memberData.id ? 'Team member updated!' : 'Team member added!');
    }
    setShowMemberModal(false);
    setSelectedMember(null);
  };

  const handleDeleteMember = async (member) => {
    if (!confirm(`Are you sure you want to remove ${member.name} from the team?`)) return;
    
    setTeam(prev => prev.filter(m => m.id !== member.id));
    toast.success(`${member.name} removed from team`);
  };

  const handleAddBonus = async (bonusData) => {
    setTeam(prev => prev.map(m => {
      if (m.id === bonusData.memberId) {
        return {
          ...m,
          earnings: {
            ...m.earnings,
            bonuses: (m.earnings?.bonuses || 0) + bonusData.amount
          },
          bonusHistory: [
            ...(m.bonusHistory || []),
            { amount: bonusData.amount, reason: bonusData.reason, date: bonusData.date }
          ]
        };
      }
      return m;
    }));
    toast.success(`$${bonusData.amount} bonus awarded!`);
    setShowBonusModal(false);
    setSelectedMember(null);
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={() => setIsAuthenticated(true)} />;
  }

  const filteredTeam = team.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate totals
  const totalEarnings = team.reduce((acc, m) => 
    acc + (m.earnings?.startup || 0) + (m.earnings?.trailing || 0) + (m.earnings?.bonuses || 0), 0
  );
  const totalDeals = team.reduce((acc, m) => acc + (m.deals || 0), 0);
  const totalBonuses = team.reduce((acc, m) => acc + (m.earnings?.bonuses || 0), 0);

  return (
    <div className="min-h-screen bg-[#FDFCF9]">
      {/* Header */}
      <header className="bg-white border-b border-[#EBE8E0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/puppoto')}
              className="flex items-center gap-2 text-[#7A7571] hover:text-[#1A1817]"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Sales Admin
              </h1>
              <p className="text-sm text-[#7A7571]">Team & Commission Management</p>
            </div>
          </div>
          
          <Button
            onClick={() => { setSelectedMember(null); setShowMemberModal(true); }}
            className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 border border-[#EBE8E0]">
            <Users className="w-8 h-8 text-[#FF7555] mb-2" />
            <div className="text-2xl font-bold text-[#1A1817]">{team.length}</div>
            <div className="text-sm text-[#7A7571]">Team Members</div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#EBE8E0]">
            <BarChart3 className="w-8 h-8 text-blue-500 mb-2" />
            <div className="text-2xl font-bold text-[#1A1817]">{totalDeals}</div>
            <div className="text-sm text-[#7A7571]">Total Deals</div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#EBE8E0]">
            <DollarSign className="w-8 h-8 text-green-500 mb-2" />
            <div className="text-2xl font-bold text-green-600">${totalEarnings.toLocaleString()}</div>
            <div className="text-sm text-[#7A7571]">Total Paid Out</div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#EBE8E0]">
            <Gift className="w-8 h-8 text-purple-500 mb-2" />
            <div className="text-2xl font-bold text-purple-600">${totalBonuses.toLocaleString()}</div>
            <div className="text-sm text-[#7A7571]">Bonuses Awarded</div>
          </div>
        </div>

        {/* Philosophy Banner */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-6 mb-8 text-white">
          <div className="flex items-start gap-4">
            <Award className="w-10 h-10 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold mb-2">Merit-Based Compensation</h2>
              <p className="text-white/80">
                People get rewarded on their merits - not their age, not their so-called skillsets, only what they can achieve. 
                Set individual commission rates for each team member based on their performance.
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7571]" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search team members..."
              className="pl-10 rounded-xl"
            />
          </div>
        </div>

        {/* Team List */}
        {loading ? (
          <div className="text-center py-12 text-[#7A7571]">Loading team...</div>
        ) : filteredTeam.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-[#EBE8E0] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#1A1817] mb-2">No team members yet</h3>
            <p className="text-[#7A7571] mb-4">Add your first team member to get started</p>
            <Button
              onClick={() => { setSelectedMember(null); setShowMemberModal(true); }}
              className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTeam.map(member => (
              <TeamMemberCard
                key={member.id}
                member={member}
                onEdit={(m) => { setSelectedMember(m); setShowMemberModal(true); }}
                onDelete={handleDeleteMember}
                onAddBonus={(m) => { setSelectedMember(m); setShowBonusModal(true); }}
              />
            ))}
          </div>
        )}
      </main>

      {/* Modals */}
      <AnimatePresence>
        {showMemberModal && (
          <MemberModal
            isOpen={showMemberModal}
            onClose={() => { setShowMemberModal(false); setSelectedMember(null); }}
            member={selectedMember}
            onSave={handleSaveMember}
          />
        )}
        {showBonusModal && selectedMember && (
          <BonusModal
            isOpen={showBonusModal}
            onClose={() => { setShowBonusModal(false); setSelectedMember(null); }}
            member={selectedMember}
            onSave={handleAddBonus}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SalesAdmin;
